import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';

const float = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); }
  33% { transform: translate(5px, -10px) rotate(1deg); }
  66% { transform: translate(-5px, -5px) rotate(-1deg); }
  100% { transform: translate(0, 0) rotate(0deg); }
`;

const glow = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
`;

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const HeroContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6rem 5% 3rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  min-height: 100vh;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 20% 30%, rgba(255, 214, 0, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(76, 175, 80, 0.06) 0%, transparent 50%),
      radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.03) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(255, 214, 0, 0.03) 90deg, transparent 180deg, rgba(76, 175, 80, 0.03) 270deg, transparent 360deg);
    animation: ${glow} 8s ease-in-out infinite;
    pointer-events: none;
    z-index: 0;
  }

  @media (max-width: 768px) {
    padding: 5rem 1.5rem 3rem;
    min-height: auto;
  }
`;

const ContentSection = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const LogoImage = styled.img`
  width: 80px;
  height: auto;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 4.5rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  color: white;
  position: relative;
  z-index: 2;
  background: linear-gradient(135deg, #ffffff 0%, #f0f0f0 50%, #ffffff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 4px 20px rgba(255, 255, 255, 0.1);
  letter-spacing: -0.02em;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transform: translateX(-100%);
    animation: ${shimmer} 3s ease-in-out infinite;
    animation-delay: 2s;
    z-index: -1;
  }

  @media (max-width: 1024px) {
    font-size: 3.5rem;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 2.5rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  z-index: 2;
  font-weight: 400;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    line-height: 1.5;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 4rem;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
    gap: 0.75rem;
    margin-bottom: 2.5rem;
  }
`;

const PrimaryButton = styled.a`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  color: #0A3B2D;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  text-decoration: none;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(255, 255, 255, 0.2);
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 255, 255, 0.3);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
  }
`;

const SecondaryButton = styled.a`
  color: white;
  text-decoration: none;
  font-weight: 500;
  padding: 1rem 2rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.05);
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);

    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0);
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 2rem;
  text-align: left;
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const FeatureIcon = styled.div`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;

  .material-icons-round {
    color: white;
    font-size: 1.5rem;
  }
`;

const FeatureTitle = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.25rem;
  font-weight: 500;
  color: white;
  margin-bottom: 0.5rem;
`;

const FeatureText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
  line-height: 1.5;
`;

// New Floating Elements
const FloatingBubble = styled.div`
  position: absolute;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  animation: ${float} ${props => props.duration || '6s'} ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
  z-index: 1;
  ${props => props.position};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);

  &:hover {
    transform: translateY(-5px) scale(1.05);
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.25);
    box-shadow:
      0 12px 40px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }

  .avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .content {
    text-align: left;
    min-width: 0;
  }

  .name {
    color: white;
    font-size: 0.9rem;
    font-weight: 500;
    margin-bottom: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .text {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.8rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .status {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #4CAF50;
    margin-left: 8px;
    flex-shrink: 0;
  }
  
  @media (max-width: 1024px) {
    padding: 0.75rem;
    gap: 0.5rem;
    
    .name {
      font-size: 0.8rem;
    }
    
    .text {
      font-size: 0.75rem;
    }
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const FloatingMetric = styled(FloatingBubble)`
  flex-direction: column;
  align-items: flex-start;
  padding: 1.5rem;
  gap: 0.75rem;
  min-width: 200px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);

  .metric-value {
    color: white;
    font-size: 1.75rem;
    font-weight: 700;
    font-family: 'Space Grotesk', sans-serif;
    line-height: 1.2;
    background: linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .metric-label {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.9rem;
    white-space: normal;
    font-weight: 500;
  }
  
  @media (max-width: 1024px) {
    min-width: 160px;
    padding: 0.75rem;
    
    .metric-value {
      font-size: 1.3rem;
    }
    
    .metric-label {
      font-size: 0.8rem;
    }
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Highlight = styled.span`
  position: relative;
  display: inline-block;
  z-index: 1;
  color: white !important;
  -webkit-text-fill-color: white !important;
  background: none !important;
  -webkit-background-clip: unset !important;
  background-clip: unset !important;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  font-weight: 600;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%) rotate(-2deg);
    width: 130%;
    height: 1.4em;
    border: 4px solid #FF6B35;
    border-radius: 45% 55% 52% 48% / 48% 62% 38% 52%;
    pointer-events: none;
    z-index: -1;
    box-sizing: border-box;
    opacity: 0.95;
    filter: drop-shadow(0 4px 12px rgba(255, 107, 53, 0.4));
    animation: ${glow} 3s ease-in-out infinite;
    background: transparent;
  }

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%) rotate(1deg);
    width: 125%;
    height: 1.3em;
    border: 3px solid #FF8C42;
    border-radius: 52% 48% 45% 55% / 55% 45% 55% 45%;
    pointer-events: none;
    z-index: -2;
    box-sizing: border-box;
    opacity: 0.7;
    filter: drop-shadow(0 2px 8px rgba(255, 140, 66, 0.3));
    animation: ${glow} 3s ease-in-out infinite;
    animation-delay: 0.5s;
    background: transparent;
  }
`;

const Hero = () => {
  const [isMobile, setIsMobile] = React.useState(false);
  
  React.useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);
  
  return (
    <HeroContainer>
      <ContentSection>
        {!isMobile && (
          <>
            <FloatingBubble
              position="top: -20px; left: -180px;"
              duration="7s"
              delay="0s"
            >
              <div className="avatar">
                <span className="material-icons-round">person</span>
              </div>
              <div className="content">
                <div className="name">Sarah M.</div>
                <div className="text">Just enrolled in benefits</div>
              </div>
              <div className="status" />
            </FloatingBubble>

            <FloatingMetric
              position="top: 100px; right: -150px;"
              duration="8s"
              delay="1s"
            >
              <span className="material-icons-round">trending_up</span>
              <div className="metric-value">98%</div>
              <div className="metric-label">Employee satisfaction rate</div>
            </FloatingMetric>

            <FloatingBubble
              position="bottom: -60px; left: -220px;"
              duration="6s"
              delay="2s"
            >
              <div className="avatar">
                <span className="material-icons-round">schedule</span>
              </div>
              <div className="content">
                <div className="name">Time saved</div>
                <div className="text">4 hours per enrollment</div>
              </div>
            </FloatingBubble>
          </>
        )}

        <Title>
          Empowering Your Workforce. Elevating Your <Highlight>Benefits</Highlight>.
        </Title>
        <Subtitle>
          Tailored employee benefits solutions for a secure, prosperous future. 
          Experience expert advisory, flexible plans, and holistic well-being through the greatest B2B solution—combining unmatched industry expertise, tailored services, and scalable innovation to drive your business forward.
          powered by Surevest.
        </Subtitle>
        <ButtonGroup>
          <PrimaryButton href="#contact">Request a demo</PrimaryButton>
          <SecondaryButton href="https://payroll.pss-group.co.za/register" target="_blank" rel="noopener noreferrer">Explore platform</SecondaryButton>
        </ButtonGroup>
        <FeaturesGrid>
          {/* Remove the three FeatureCard components for AI-Powered Benefits, Streamlined Enrollment, and Analytics Dashboard from the FeaturesGrid in the Hero section */}
        </FeaturesGrid>
      </ContentSection>
    </HeroContainer>
  );
};

export default Hero; 